/**
 * Metrics § Schedule (§8.5 + §8.6 merged, redesign § Schedule).
 *
 * One island for the former Posting-schedule and Best-day-&-time sections: a stats row (Average
 * posts / week · Best day · Best hour · Best performing day · Best performing hour, each with its
 * server tooltip inside the stat), the posting calendar heatmap, and the day × hour matrix. The
 * two DTO sections stay independently `ok`/`empty` — an empty half renders its server `reason`
 * without blanking the other half (§12.5 rule 5). The time-series chart and "Posts in range"
 * moved to Overview per the redesign (`totalPosts` stays in the DTO, simply unrendered).
 *
 * Each grid has its own Volume | Performance toggle (§8.6). Both faces ship in the one metrics
 * response, so toggling is client-only — it re-renders, it never refetches. Volume answers "when
 * does this author post", performance answers "when do their posts do well", and the two are
 * independent because a reader usually wants one of each.
 */

import { useRef, useState } from 'react';
import type {
  BestTimeCellDTO,
  BestTimesData,
  PostingScheduleData,
  ScheduleHeatmapMode,
  Section,
  Tooltip,
} from '@lw/shared';
import { EmptyState, TooltipPopover } from '../../../components';
import { cx } from '../../../components/cx';
import { formatInteger } from '../../../lib/format';
import { WEEKDAY_LABEL, WEEKDAY_SHORT, hourLabel } from '../../../lib/labels';
import { CalendarHeatmap } from './CalendarHeatmap';

/** Below this the server withholds `perf.shade`; the hover says so instead of colouring. */
const LOW_SAMPLE_NOTE = ' — low sample';

/** `Volume | Performance` — two `aria-pressed` buttons in a part-title row. */
function ModeToggle({
  mode,
  onChange,
  label,
}: {
  mode: ScheduleHeatmapMode;
  onChange: (mode: ScheduleHeatmapMode) => void;
  label: string;
}) {
  return (
    <span className="hm-modes" role="group" aria-label={label}>
      {(['volume', 'performance'] as ScheduleHeatmapMode[]).map((option) => (
        <button
          key={option}
          type="button"
          className={cx('hm-mode', mode === option && 'is-active')}
          aria-pressed={mode === option}
          onClick={() => onChange(option)}
        >
          {option === 'volume' ? 'Volume' : 'Performance'}
        </button>
      ))}
    </span>
  );
}

/** The server's one-line explanation when no author has a usable baseline (§8.6). */
function PerformanceNote({ reason }: { reason: string | undefined }) {
  if (reason === undefined) return null;
  return <p className="hm-note faint">{reason}</p>;
}

/* ---------------------------------------------------------- day × hour matrix */

/** 38 + 24 × 35 = 878 px — the full day fits the ~894 px card body without scrolling. */
const CELL_W = 32;
const CELL_H = 20;
const GAP = 3;
const LEFT = 38;
const TOP = 18;
const LEVELS = 5;

function level(value: number | null, max: number): number {
  // `null` is "nobody reported engagement here", which colours like an empty slot, not like a 0.
  if (value === null || value <= 0 || max <= 0) return 0;
  return Math.min(LEVELS, Math.max(1, Math.ceil((value / max) * LEVELS)));
}

/** Cell class per face: the volume ramp, or the server's chip shade (neutral when withheld). */
function cellClass(cell: BestTimeCellDTO, max: number, mode: ScheduleHeatmapMode): string {
  if (mode === 'volume') return `hm-cell hm-cell--${String(level(cell.totalEngagement, max))}`;
  return cell.perf.shade === null ? 'hm-cell hm-cell--0' : `hm-cell hm-perf--${cell.perf.shade}`;
}

/** Dense 7 × 24 grid with a dark hover tooltip (weekday · hour · the active face's figure). */
function DayHourMatrix({ data, mode }: { data: BestTimesData; mode: ScheduleHeatmapMode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; cell: BestTimeCellDTO } | null>(null);

  const width = LEFT + 24 * (CELL_W + GAP);
  const height = TOP + 7 * (CELL_H + GAP);

  const show = (cell: BestTimeCellDTO) => {
    const scrollLeft = scrollRef.current?.scrollLeft ?? 0;
    setTip({
      x: LEFT + cell.hour * (CELL_W + GAP) + CELL_W / 2 - scrollLeft,
      y: TOP + (cell.dow - 1) * (CELL_H + GAP),
      cell,
    });
  };

  return (
    <div className="svg-hover">
      <div className="svg-hover__scroll" ref={scrollRef}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${String(width)} ${String(height)}`}
          role="img"
          aria-label={
            mode === 'volume'
              ? `Engagement by weekday and hour, ${data.timezone}`
              : `Performance by weekday and hour, ${data.timezone}`
          }
          onMouseLeave={() => setTip(null)}
        >
          {/* All 24 columns labelled — "00".."23", not hourLabel()'s "00:00", which will not fit. */}
          {Array.from({ length: 24 }, (_, hour) => (
            <text
              key={hour}
              className="hm-axis"
              x={LEFT + hour * (CELL_W + GAP) + CELL_W / 2}
              y={11}
              textAnchor="middle"
            >
              {String(hour).padStart(2, '0')}
            </text>
          ))}

          {[1, 2, 3, 4, 5, 6, 7].map((dow) => (
            <text
              key={dow}
              className="hm-axis"
              x={0}
              y={TOP + (dow - 1) * (CELL_H + GAP) + CELL_H - 6}
            >
              {WEEKDAY_SHORT[dow]}
            </text>
          ))}

          {data.cells.map((cell) => (
            <rect
              key={`${String(cell.dow)}-${String(cell.hour)}`}
              x={LEFT + cell.hour * (CELL_W + GAP)}
              y={TOP + (cell.dow - 1) * (CELL_H + GAP)}
              width={CELL_W}
              height={CELL_H}
              rx={3}
              className={cellClass(cell, data.maxTotalEngagement, mode)}
              onMouseEnter={() => show(cell)}
              onMouseLeave={() => setTip(null)}
            />
          ))}
        </svg>
      </div>

      {tip !== null ? (
        <div className="svg-tip" style={{ left: tip.x, top: tip.y }}>
          <div className="t-label">
            {WEEKDAY_LABEL[tip.cell.dow]} · {hourLabel(tip.cell.hour)}
          </div>
          {mode === 'volume' ? (
            <div className="t-row">
              <span className="t-label">Total eng.</span>
              {/* Separators only: presenting a raw chart value is rendering, not client math (§12.5).
                  `formatInteger` already renders null as the same em dash. */}
              <span className="t-value">{formatInteger(tip.cell.totalEngagement)}</span>
            </div>
          ) : tip.cell.perf.n === 0 ? (
            <div className="t-value">No posts</div>
          ) : (
            <div className="t-value">
              {tip.cell.perf.display} median · {formatInteger(tip.cell.perf.n)}{' '}
              {tip.cell.perf.n === 1 ? 'post' : 'posts'}
              {/* `shade === null` with posts present is exactly the sub-threshold case (§8.6). */}
              {tip.cell.perf.shade === null ? LOW_SAMPLE_NOTE : ''}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------- stats */

/** Standalone stat with its (i) in the card's top-right corner — one placement app-wide. */
function ScheduleStat({
  label,
  value,
  tooltip,
  sub,
}: {
  label: string;
  value: string;
  tooltip: Tooltip | undefined;
  sub?: string;
}) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">
        <span>{label}</span>
      </div>
      <span className="stat-card__value">{value}</span>
      {sub !== undefined ? <span className="stat-card__sub">{sub}</span> : null}
      {tooltip !== undefined ? (
        <TooltipPopover tooltip={tooltip} align="left" label={`How ${label} is calculated`} />
      ) : null}
    </div>
  );
}

/** `{display} median · {n} posts` — composed from server strings, like the "N total eng." subs. */
function perfSub(slot: { display: string; n: number } | null): string | undefined {
  if (slot === null) return undefined;
  return `${slot.display} median · ${formatInteger(slot.n)} ${slot.n === 1 ? 'post' : 'posts'}`;
}

/* ------------------------------------------------------------------ section */

export type ScheduleSectionProps = {
  schedule: Section<PostingScheduleData> | undefined;
  bestTimes: Section<BestTimesData> | undefined;
  timezone: string;
  /** Persisted per view; the two grids are independent (§8.6). */
  calendarMode: ScheduleHeatmapMode;
  matrixMode: ScheduleHeatmapMode;
  onCalendarModeChange: (mode: ScheduleHeatmapMode) => void;
  onMatrixModeChange: (mode: ScheduleHeatmapMode) => void;
  /** Opens the popup listing that calendar day's posts (the matrix has no equivalent: its
      slots are weekday x hour pools, not a date range the feed can be filtered to). */
  onDayClick?: (index: number) => void;
};

export function ScheduleSection({
  schedule,
  bestTimes,
  timezone,
  calendarMode,
  matrixMode,
  onCalendarModeChange,
  onMatrixModeChange,
  onDayClick,
}: ScheduleSectionProps) {
  if (schedule === undefined && bestTimes === undefined) return null;

  const schedOk = schedule !== undefined && schedule.state === 'ok' ? schedule : null;
  const bestOk = bestTimes !== undefined && bestTimes.state === 'ok' ? bestTimes : null;
  const tz = bestOk !== null ? bestOk.data.timezone : timezone;
  const headTooltip = schedule?.tooltip ?? bestTimes?.tooltip;

  const head = (
    <div className="section-head">
      <h2 className="section-title metrics-title">
        Schedule
        {headTooltip !== undefined ? (
          <TooltipPopover tooltip={headTooltip} label="How Schedule is calculated" />
        ) : null}
      </h2>
    </div>
  );

  if (schedOk === null && bestOk === null) {
    return (
      <section className="section">
        {head}
        <div className="card">
          <EmptyState
            title="Not enough data yet"
            description={schedule?.reason ?? bestTimes?.reason}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      {head}
      <div className="card">
        <div className="card__body">
          <div className="sched-top">
            <div className="sched-stats">
              {schedOk !== null ? (
                <ScheduleStat
                  label="Average posts / week"
                  value={schedOk.data.avgPostsPerWeek.display}
                  tooltip={schedOk.data.avgPostsPerWeek.tooltip}
                />
              ) : null}
              {bestOk !== null ? (
                <>
                  <ScheduleStat
                    label="Best day"
                    value={bestOk.data.bestDay?.label ?? '—'}
                    tooltip={bestOk.tooltip}
                    sub={
                      bestOk.data.bestDay !== null
                        ? `${formatInteger(bestOk.data.bestDay.totalEngagement)} total eng.`
                        : undefined
                    }
                  />
                  <ScheduleStat
                    label="Best hour"
                    value={bestOk.data.bestHour?.label ?? '—'}
                    tooltip={bestOk.tooltip}
                    sub={
                      bestOk.data.bestHour !== null
                        ? `${formatInteger(bestOk.data.bestHour.totalEngagement)} total eng.`
                        : undefined
                    }
                  />
                  {/* The Σ-based and the performance answers stand side by side: they ask
                      different questions and often disagree (§8.6). */}
                  <ScheduleStat
                    label="Best performing day"
                    value={bestOk.data.bestPerformingDay?.label ?? '—'}
                    tooltip={bestOk.data.performanceTooltip}
                    sub={perfSub(bestOk.data.bestPerformingDay)}
                  />
                  <ScheduleStat
                    label="Best performing hour"
                    value={bestOk.data.bestPerformingHour?.label ?? '—'}
                    tooltip={bestOk.data.performanceTooltip}
                    sub={perfSub(bestOk.data.bestPerformingHour)}
                  />
                </>
              ) : null}
            </div>
            <span className="sched-tz faint">{tz}</span>
          </div>

          {schedOk !== null ? (
            <div className="sched-part">
              <h3 className="metrics-parttitle">
                {calendarMode === 'volume' ? 'Posting calendar' : 'Performance calendar'}
                <TooltipPopover
                  tooltip={
                    calendarMode === 'volume'
                      ? schedOk.data.heatmapTooltip
                      : schedOk.data.heatmap.performanceTooltip
                  }
                  label={
                    calendarMode === 'volume'
                      ? 'How the posting calendar is calculated'
                      : 'How the performance calendar is calculated'
                  }
                />
                <ModeToggle
                  mode={calendarMode}
                  onChange={onCalendarModeChange}
                  label="Posting calendar face"
                />
              </h3>
              {calendarMode === 'performance' ? (
                <PerformanceNote reason={schedOk.data.heatmap.performanceReason} />
              ) : null}
              <CalendarHeatmap
                days={schedOk.data.heatmap.days}
                maxPosts={schedOk.data.heatmap.maxPosts}
                timezone={tz}
                mode={calendarMode}
                onDayClick={onDayClick}
              />
            </div>
          ) : schedule !== undefined ? (
            <EmptyState title="Not enough data yet" description={schedule.reason} />
          ) : null}

          {bestOk !== null ? (
            <div className="sched-part">
              <h3 className="metrics-parttitle">
                {matrixMode === 'volume'
                  ? 'Engagement by day & hour'
                  : 'Performance by day & hour'}
                <TooltipPopover
                  tooltip={
                    matrixMode === 'volume' ? bestOk.tooltip : bestOk.data.performanceTooltip
                  }
                  label={
                    matrixMode === 'volume'
                      ? 'How engagement by day and hour is calculated'
                      : 'How performance by day and hour is calculated'
                  }
                />
                <ModeToggle
                  mode={matrixMode}
                  onChange={onMatrixModeChange}
                  label="Day and hour matrix face"
                />
              </h3>
              {matrixMode === 'performance' ? (
                <PerformanceNote reason={bestOk.data.performanceReason} />
              ) : null}
              <DayHourMatrix data={bestOk.data} mode={matrixMode} />
            </div>
          ) : bestTimes !== undefined ? (
            <EmptyState title="Not enough data yet" description={bestTimes.reason} />
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default ScheduleSection;
